import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useApp } from "@/contexts/AppContext";
import type { DayType } from "@/features/app-context/types";
import { formatAttendanceDate } from "../lib/format";
import { HISTORY_KEY } from "../queryKeys";
import { trackedDaysPrefix } from "@/hooks/queries/useTrackedDays";
import { circlesSchedulePrefix } from "@/features/circles/queryKeys";

/**
 * Two mutations powering the Attendance confirmation card:
 *
 *   • `validateAttendance`  — user confirms they went to the office
 *     on `dateStr`. Marks the day with the resolved `officeType` in
 *     AppContext and writes an archived `attendance` notification as
 *     a lightweight audit-log entry in History.
 *
 *   • `clearAttendance`  — user says "no, clear this day" — wipes the
 *     entry from `tracked_days` (AppContext.clearDays) and writes a
 *     matching audit-log row.
 *
 * The audit-log insert is best-effort: a DB failure there does NOT
 * fail the whole mutation since the user-visible state change
 * (calendar mark / clear) already happened.
 *
 * Callers can derive per-date loading state from
 * `validateAttendance.isPending && validateAttendance.variables === date`.
 */
export function useAttendanceActions() {
  const queryClient = useQueryClient();
  const { dayTypes, markedDays, markDays, clearDays } = useApp();

  // Use baseCategory for lookups — decoupled from display name
  const officeType: DayType | null =
    dayTypes.find(
      (t) => t.baseCategory === "WORK_OFFICE" || t.id === "office",
    ) ?? null;

  const validateAttendance = useMutation({
    mutationFn: async (dateStr: string) => {
      // Preserve the day's OWN designation (office / travel / visit) when
      // confirming — the bell now surfaces every office-equivalent type, so
      // forcing `office` here would silently rewrite a Business Travel or
      // Client Visit day into a plain Office day.
      const existing = markedDays.find((d) => d.date === dateStr);
      const typeId = existing?.typeId ?? officeType?.id;
      if (!typeId) return;
      await markDays({
        dates: [dateStr],
        typeId,
        // User actively CONFIRMED — flip the row from `unconfirmed` to
        // `confirmed` regardless of whether the date is past/future.
        // Without the explicit `status` override, `markDays` would
        // re-derive it from the date comparison and silently leave a
        // past unconfirmed day right back where it started.
        status: "confirmed",
      });

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { error } = await supabase.from("notifications").insert({
          user_id:     session.user.id,
          type:        "attendance",
          title:       "Attendance Confirmed",
          message:     `For ${formatAttendanceDate(dateStr)}`,
          is_read:     true,
          is_archived: true,
        });
        if (error) {
          // Non-critical background failure — audit log insert failing
          // must not block the primary mark operation.
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
      queryClient.invalidateQueries({ queryKey: trackedDaysPrefix });
      queryClient.invalidateQueries({ queryKey: circlesSchedulePrefix });
    },
    onError: (err: Error) => {
      toast({
        title:       "Failed to confirm attendance",
        description: err.message,
        variant:     "destructive",
      });
    },
  });

  const clearAttendance = useMutation({
    mutationFn: async (dateStr: string) => {
      // Primary effect: DELETE the tracked_days row for this specific
      // user + date. Bug 4 (Sprint 2) — the user reported that clicking
      // "No, clear" on the Bell's AttendanceCard was only dismissing
      // the in-memory prompt and leaving the row in the DB. The root
      // cause was two-fold:
      //
      //   1. AppContext.clearDays did DELETE the row but never
      //      invalidated the shared `["trackedDays"]` React Query
      //      cache that the Dashboard/Analytics/Workspace hub all
      //      read from. The calendar grid happily kept rendering the
      //      ghost office mark from stale cache.
      //   2. The `onSuccess` below only invalidated HISTORY_KEY.
      //
      // We now explicitly ensure the DELETE happens, then surgically
      // invalidate every query that renders attendance so the "No"
      // button immediately and visibly wipes the day everywhere.
      let userId: string | null = null;
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) userId = session.user.id;

      // Belt-and-suspenders DELETE. Even if AppContext.clearDays is
      // ever swapped for a no-op mock (tests) or silently short-
      // circuits on "session expired" toast path, this guarantees the
      // row is gone from the DB before we declare success.
      if (userId) {
        const { error: deleteErr } = await supabase
          .from("tracked_days")
          .delete()
          .eq("user_id", userId)
          .eq("date", dateStr);
        if (deleteErr) throw new Error(deleteErr.message);
      }

      // Mirror the delete into the AppContext in-memory markedDays
      // state so the Bell's `daysToConfirm` lane recomputes instantly
      // without waiting on a React Query refetch.
      await clearDays([dateStr]);

      if (userId) {
        const { error } = await supabase.from("notifications").insert({
          user_id:     userId,
          type:        "attendance",
          title:       "Attendance Cleared",
          message:     `For ${formatAttendanceDate(dateStr)}`,
          is_read:     true,
          is_archived: true,
        });
        if (error) {
          // Non-critical background failure — audit log insert failing
          // must not block the primary clear operation.
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
      queryClient.invalidateQueries({ queryKey: trackedDaysPrefix });
      queryClient.invalidateQueries({ queryKey: circlesSchedulePrefix });
    },
    onError: (err: Error) => {
      toast({
        title:       "Failed to clear attendance",
        description: err.message,
        variant:     "destructive",
      });
    },
  });

  return { officeType, validateAttendance, clearAttendance };
}
